import React from 'react';
import { CategoriaTorneio, VagasPorCategoria } from '../services/torneioService';
import { getStatusCategoriaTorneioDisplay } from '../services/categoriaTorneioService';
import { InscricaoTorneio } from '../services/inscricaoTorneioService';
import { StatusInscricao } from '../types/inscricao';
import { getVagasInfo } from '../utils/vagasUtils';
import { StatusCategoriaTorneio } from '../types/categoriaTorneio';

interface TournamentCategoriasTabProps {
  categorias: CategoriaTorneio[];
  torneioId: string;
  onInscreva: () => void;
  onCategoryClick: (categoriaId: string) => void;
  inscricoes?: InscricaoTorneio[];
  contagemVagas?: VagasPorCategoria[];
  isAtleta?: boolean;
  onPagarInscricao?: (pagamentoId: string) => void;
  exibirFilaEspera?: boolean | null;
  exibirInscritos?: boolean | null;
}

const getGeneroLabel = (genero?: string | null): string => {
  switch (genero) {
    case 'MASCULINO': return 'Masc.';
    case 'FEMININO': return 'Fem.';
    case 'MISTO': return 'Misto';
    default: return '';
  }
};

const getTipoLabel = (numeroAtletas?: number): string => {
  if (numeroAtletas === 1) return 'Simples';
  if (numeroAtletas === 2) return 'Duplas';
  return '';
};

const formatPreco = (preco?: number | null): string => {
  if (preco == null || preco === 0) return 'Grátis';
  return preco.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const TournamentCategoriasTab: React.FC<TournamentCategoriasTabProps> = ({
  categorias,
  torneioId,
  onInscreva,
  onCategoryClick,
  inscricoes,
  contagemVagas,
  isAtleta,
  onPagarInscricao,
  exibirFilaEspera,
  exibirInscritos,
}) => {
  if (categorias.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">Nenhuma categoria cadastrada</p>
      </div>
    );
  }

  const renderInscricaoCell = (categoria: CategoriaTorneio) => {
    const inscricao = inscricoes?.find(i => i.categoriaTorneio?.id === categoria.id);

    if (inscricao) {
      if (inscricao.status === StatusInscricao.CONFIRMADA) {
        return <span className="text-success fw-bold">Inscrição confirmada</span>;
      }
      if (inscricao.status === StatusInscricao.PENDENTE) {
        return (
          <button
            className="btn btn-warning btn-sm"
            onClick={() => inscricao.pagamentoInscricao?.id && onPagarInscricao?.(inscricao.pagamentoInscricao.id)}
            disabled={!inscricao.pagamentoInscricao?.id}
          >
            Pagar inscrição
          </button>
        );
      }
      if (inscricao.status === StatusInscricao.FILA_ESPERA) {
        if (exibirFilaEspera) {
          return <span className="text-info fw-bold">Fila de Espera</span>;
        }
        return null;
      }
      return null;
    }

    if (categoria.status === StatusCategoriaTorneio.INSCRICAO_ABERTA) {
      return (
        <button className="btn btn-primary btn-sm" onClick={onInscreva}>
          Inscreva-se
        </button>
      );
    }

    return null;
  };

  return (
    <div className="table-responsive">
      <table className="table table-hover">
        <thead>
          <tr>
            <th>Categoria</th>
            <th>Tipo</th>
            <th>Preço</th>
            <th>Vagas</th>
            <th>Status</th>
            {isAtleta && <th>Inscrição</th>}
            {!isAtleta && <th></th>}
          </tr>
        </thead>
        <tbody>
          {categorias.map((categoria) => {
            const statusDisplay = getStatusCategoriaTorneioDisplay(categoria.status);
            const genero = getGeneroLabel(categoria.genero);
            const tipo = getTipoLabel(categoria.numeroAtletas);
            const tipoCompleto = [genero, tipo].filter(Boolean).join(' ');

            // Compute vagas display using centralized utility
            let vagasDisplay: string;
            if (categoria.vagas == null) {
              vagasDisplay = '-';
            } else if (contagemVagas && exibirInscritos) {
              const info = getVagasInfo(categoria.id, categoria.vagas, contagemVagas, null);
              vagasDisplay = `${info.ocupadas}/${categoria.vagas}`;
            } else {
              vagasDisplay = `${categoria.vagas}`;
            }

            return (
              <tr key={categoria.id}>
                <td>
                  <strong
                    className="text-primary"
                    style={{ cursor: 'pointer' }}
                    onClick={() => onCategoryClick(categoria.id)}
                  >
                    {categoria.nome}
                  </strong>
                </td>
                <td>{tipoCompleto || '-'}</td>
                <td>{formatPreco(categoria.precoInscricao)}</td>
                <td>{vagasDisplay}</td>
                <td>
                  <span className={`badge ${statusDisplay.color}`}>
                    {statusDisplay.label}
                  </span>
                </td>
                {isAtleta && (
                  <td>{renderInscricaoCell(categoria)}</td>
                )}
                {!isAtleta && (
                  <td>
                    {categoria.status === StatusCategoriaTorneio.INSCRICAO_ABERTA && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={onInscreva}
                      >
                        Inscreva-se
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TournamentCategoriasTab;
